// In-page viewer for PDFs and photos — opens over the page; step through the other files with ← / →.
// Anything it can't show (Word, Excel, zip…) downloads as before.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { downloadFile, viewUrl } from '../lib/api';
import { Button, errText, useToast } from './kit';

export type Viewable = { id: string; name: string; mime?: string | null; kind?: string | null };

export const isPdf = (f: Viewable) => /pdf/i.test(f.mime ?? '') || /\.pdf$/i.test(f.name);
export const isImage = (f: Viewable) => /^image\/(jpe?g|png|gif|webp|bmp|svg\+xml)$/i.test(f.mime ?? '') || /\.(jpe?g|png|gif|webp|bmp)$/i.test(f.name);
export const canView = (f: Viewable) => f.kind !== 'link' && (isPdf(f) || isImage(f));

type Open = (file: Viewable, list?: Viewable[]) => void;
const Ctx = createContext<Open>(() => {});

/** open(file, list): shows the file if it can be viewed (else downloads / opens the link). */
export const useViewer = () => useContext(Ctx);

export function ViewerHost({ children }: { children: ReactNode }) {
  const toast = useToast();
  const [list, setList] = useState<Viewable[]>([]);
  const [index, setIndex] = useState(-1);
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState('');

  const open = useCallback<Open>(
    (file, all) => {
      if (!canView(file)) {
        void downloadFile(file.id).catch((e) => toast({ tone: 'error', text: errText(e) }));
        return;
      }
      const viewable = (all ?? [file]).filter(canView);
      const i = viewable.findIndex((f) => f.id === file.id);
      setList(i >= 0 ? viewable : [file]);
      setIndex(i >= 0 ? i : 0);
    },
    [toast],
  );

  const file = index >= 0 ? list[index] : null;
  const close = useCallback(() => {
    setIndex(-1);
    setUrl(null);
  }, []);

  useEffect(() => {
    if (!file) return;
    let live = true;
    setUrl(null);
    setErr('');
    viewUrl(file.id)
      .then((r) => live && setUrl(r.url))
      .catch((e) => live && setErr(errText(e)));
    return () => {
      live = false;
    };
  }, [file]);

  useEffect(() => {
    if (!file) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight' && index < list.length - 1) setIndex(index + 1);
      else if (e.key === 'ArrowLeft' && index > 0) setIndex(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [file, index, list.length, close]);

  // phones without a built-in PDF viewer (e.g. Chrome on Android) get "open in a new tab" instead
  const nativePdf = typeof navigator === 'undefined' || (navigator as Navigator & { pdfViewerEnabled?: boolean }).pdfViewerEnabled !== false;

  return (
    <Ctx.Provider value={open}>
      {children}
      {file ? (
        <div className="viewer" role="dialog" aria-modal="true" aria-label={`Viewing ${file.name}`}>
          <div className="viewer-head">
            <strong className="viewer-name" title={file.name}>
              {file.name}
            </strong>
            {list.length > 1 ? (
              <span className="viewer-nav">
                <Button kind="ghost" aria-label="Previous file" disabled={index === 0} onClick={() => setIndex(index - 1)}>
                  ←
                </Button>
                <span className="small">
                  {index + 1} / {list.length}
                </span>
                <Button kind="ghost" aria-label="Next file" disabled={index === list.length - 1} onClick={() => setIndex(index + 1)}>
                  →
                </Button>
              </span>
            ) : null}
            <span className="viewer-actions">
              {url ? (
                <a className="btn btn-ghost viewer-link" href={url} target="_blank" rel="noopener">
                  Open in new tab
                </a>
              ) : null}
              <Button kind="ghost" onClick={() => void downloadFile(file.id).catch((e) => toast({ tone: 'error', text: errText(e) }))}>
                Download
              </Button>
              <button type="button" className="icon-btn" aria-label="Close viewer" onClick={close}>
                ×
              </button>
            </span>
          </div>
          <div className="viewer-body" onMouseDown={(e) => e.target === e.currentTarget && close()}>
            {err ? (
              <div className="error-box">{err}</div>
            ) : !url ? (
              <span className="viewer-msg">Loading…</span>
            ) : isImage(file) ? (
              <img src={url} alt={file.name} className="viewer-img" />
            ) : nativePdf ? (
              <iframe key={url} src={url} title={file.name} className="viewer-frame" />
            ) : (
              <span className="viewer-msg">
                This device can't show PDFs inside the page.{' '}
                <a href={url} target="_blank" rel="noopener" style={{ color: '#fff' }}>
                  Open it in a new tab
                </a>
              </span>
            )}
          </div>
        </div>
      ) : null}
    </Ctx.Provider>
  );
}
